'use client';
import { Input } from '@ui/components/moleculas';
import { IMessagePayload } from '@ui/components';
import { useFiles, usePostHogFeatureFlag } from '@ui/hooks';
import { MAX_FILES } from '@ui/hooks/file/constants';
import { useCreatorVideoUploadPermission } from '@ui/hooks/use-creator-video-upload-permission';
import dynamic from 'next/dynamic';
import { useEffect, useState, useRef } from 'react';

const RichTextEditor = dynamic(
  () => import('@ui/components/ui/rte-editor/rte-editor').then((mod) => mod.RichTextEditor),
  { ssr: false },
);

const initialState: IMessagePayload = {
  send_email: false,
  files: {
    images: [],
  },
  name: '',
  description: '',
  post: true,
  type: 'post',
};

interface BasePostCreationFormProps {
  initialValues?: Partial<IMessagePayload>;
  onSubmit: (
    message: IMessagePayload,
    description: string,
    mentions: string[],
    clearEditorContent: () => void,
    clearMessage: () => void,
    discardFiles: () => void,
  ) => Promise<void>;
  isSubmitDisabled?: boolean;
  isLoading?: boolean;
  authorAvatarURL?: string;
  userType?: 'creator' | 'fan';
  currentUser?: 'creator' | 'fan';
  placeholder?: string;
  editorLabel?: string;
  buttonLabel?: string;
  showCancelButton?: boolean;
  enableSuggestions?: boolean;
  initialMention?: { id: string; label: string };
  enableSendEmailToggle?: boolean;
  showSaveDraftButton?: boolean;
  authorAvatarFallbackName?: string;
  onCancel?: () => void;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
}

export const BasePostCreationForm = ({
  initialValues = {
    ...initialState,
  },
  onSubmit,

  isSubmitDisabled = false,
  isLoading = false,
  authorAvatarURL,
  userType = 'creator',
  currentUser = 'creator',
  placeholder = 'Compose content...',
  editorLabel = 'Create content',
  buttonLabel = 'Submit',
  showCancelButton = false,
  enableSuggestions = false,
  initialMention,
  enableSendEmailToggle = false,
  showSaveDraftButton = false,
  authorAvatarFallbackName,
  onCancel,
  creatorData,
}: BasePostCreationFormProps) => {
  const [message, setMessage] = useState<IMessagePayload>({
    send_email: false,
    files: { images: [] },
    name: '',
    description: '',
    post: true,
    type: 'post',
    ...initialValues,
  });
  const isVideoStreamUploadEnabled = usePostHogFeatureFlag('video-stream-upload');
  const { isVideoUploadEnabled } = useCreatorVideoUploadPermission(creatorData);
  const { files, onSelectFiles, deleteFile, onDiscardFiles, error } = useFiles({
    allowVideos: isVideoStreamUploadEnabled && isVideoUploadEnabled ? true : false,
  });
  const [displayError, setDisplayError] = useState<string | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setMessage((state) => ({ ...state, files: { images: files } }));
  }, [files]);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      setDisplayError(error);
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      errorTimerRef.current = setTimeout(() => {
        setDisplayError(null);
      }, 5000);
    }

    return () => {
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
    };
  }, [error]);

  const onCancelEditor = () => {
    setMessage(initialState);
    onDiscardFiles();
    onCancel?.();
  };

  const handleSendEmailToggle = (value: boolean) => {
    setMessage((prev) => ({ ...prev, send_email: value }));
  };

  const handleSubmit = (
    description: string,
    mentions: string[],
    isSaveDraft: boolean,
    clearEditor: () => void,
  ) =>
    onSubmit(
      { ...message, draft: isSaveDraft },
      description,
      mentions,
      clearEditor,
      () => setMessage(initialState),
      onDiscardFiles,
    );

  return (
    <div className="flex flex-col gap-1">
      <Input
        value={message.name}
        type="text"
        isHideError={true}
        placeholder="Title"
        onChange={(event: any) => setMessage({ ...message, name: event.target.value })}
      />
      {displayError && (
        <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600 font-medium">{displayError}</p>
        </div>
      )}
      <RichTextEditor
        deleteFile={deleteFile}
        onSelectFiles={onSelectFiles}
        isSubmitButtonDisabled={isSubmitDisabled || !Boolean(message.name)}
        authorAvatarURL={authorAvatarURL}
        onChange={handleSubmit}
        value={message.description || ''}
        userType={userType}
        currentUser={currentUser}
        files={files}
        isLoading={isLoading}
        exceededFileLimit={files.length >= MAX_FILES}
        placeholder={placeholder}
        editorLabel={editorLabel}
        buttonLabel={buttonLabel}
        maxFiles={MAX_FILES}
        showCancelButton={showCancelButton}
        onCancel={onCancelEditor}
        enableSuggestions={enableSuggestions}
        initialMention={initialMention}
        sendEmail={message.send_email}
        onToggleSendEmail={enableSendEmailToggle ? handleSendEmailToggle : undefined}
        editorClassName="min-h-[80px]"
        showSaveDraftButton={showSaveDraftButton}
        authorAvatarFallbackName={authorAvatarFallbackName}
        creatorDisplayName={undefined}
        allowVideos={isVideoStreamUploadEnabled && isVideoUploadEnabled ? true : false}
      />
    </div>
  );
};
